import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AuthStoreAction, RootStoreState } from 'src/app/root-store';
import { PreferencesSelectors } from 'src/app/root-store/preferences-store';
import { AuthService } from './auth.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  // Fallback timeout used until preferences are loaded from store.
  // 0 means automatic idle logout is disabled.
  private readonly defaultIdleTimeoutMinutes = 0;
  // Idle timeout duration (resolved from preferences store).
  private idleTimeoutMs = this.defaultIdleTimeoutMinutes * 60 * 1000;
  // Shared keys used to coordinate activity and logout across tabs.
  private readonly idleLastActivityStorageKey = 'fo-idle-last-activity';
  private readonly logoutBroadcastStorageKey = 'fo-auth-logout-event';
  // Limits localStorage writes while user is continuously active.
  private readonly activitySyncThrottleMs = 10000;
  // Unique identifier for the current browser tab.
  private readonly currentTabId = `${Date.now()}-${Math.random()}`;

  // Internal runtime state for idle tracking and auth synchronization.
  private idleTrackingActive = false;
  private isAuthenticated = false;
  private lastActivityTimestamp = 0;
  private lastActivitySyncTimestamp = 0;
  private idleTimerId: ReturnType<Window['setTimeout']> | null = null;
  private removeActivityListeners: Array<() => void> = [];
  private removeStorageListener: () => void;
  private preferencesTimeoutSubscription: Subscription;
  private skipNextLogoutBroadcast = false;

  constructor(
    private tokenService: TokenService,
    private authService: AuthService,
    private store: Store<RootStoreState.State>,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.bindIdleTimeoutFromPreferences();
  }

  // Starts or stops idle tracking based on auth state transitions.
  handleAuthTransition(wasAuthenticated: boolean, isAuthenticated: boolean): void {
    this.isAuthenticated = isAuthenticated;

    if (isAuthenticated) {
      this.startIdleTracking();
      return;
    }

    this.stopIdleTracking();

    if (wasAuthenticated && !this.skipNextLogoutBroadcast && !this.isCurrentTabTokenValid()) {
      this.broadcastLogoutEvent('auth-changed');
    }

    this.skipNextLogoutBroadcast = false;
  }

  // Cleans up timers and listeners when the hosting component is destroyed.
  destroy(): void {
    this.stopIdleTracking();
    if (this.preferencesTimeoutSubscription) {
      this.preferencesTimeoutSubscription.unsubscribe();
      this.preferencesTimeoutSubscription = null;
    }
  }

  // Enables activity listeners, cross-tab listeners, and initial idle scheduling.
  private startIdleTracking(): void {
    if (this.idleTrackingActive || !this.hasWindow() || !this.isIdleTimeoutEnabled()) {
      return;
    }

    this.idleTrackingActive = true;
    this.bindActivityListeners();
    this.bindStorageListener();
    this.updateActivity(true);
    this.scheduleIdleCheck();
  }

  // Disables idle tracking and removes all registered listeners/timers.
  private stopIdleTracking(): void {
    this.idleTrackingActive = false;

    if (this.idleTimerId !== null) {
      this.document.defaultView?.clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }

    if (this.removeStorageListener) {
      this.removeStorageListener();
      this.removeStorageListener = null;
    }

    this.removeActivityListeners.forEach((remove) => remove());
    this.removeActivityListeners = [];
  }

  // Subscribes to user interaction events that represent activity.
  private bindActivityListeners(): void {
    if (this.removeActivityListeners.length > 0) {
      return;
    }

    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    const activityEvents: Array<keyof DocumentEventMap> = ['mousemove', 'mousedown', 'click', 'scroll', 'keydown', 'touchstart'];
    this.removeActivityListeners = activityEvents.map((eventName) => {
      const listener = () => this.updateActivity();
      this.document.addEventListener(eventName, listener, { passive: true });
      return () => this.document.removeEventListener(eventName, listener);
    });
  }

  // Listens for localStorage changes from other tabs.
  private bindStorageListener(): void {
    if (this.removeStorageListener) {
      return;
    }

    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    const listener = (event: StorageEvent) => {
      if (!event || !event.key) {
        return;
      }

      if (event.key === this.idleLastActivityStorageKey) {
        const syncedTimestamp = Number(event.newValue);
        if (syncedTimestamp && syncedTimestamp > this.lastActivityTimestamp) {
          this.lastActivityTimestamp = syncedTimestamp;
          this.scheduleIdleCheck();
        }
      }

      if (event.key === this.logoutBroadcastStorageKey && event.newValue) {
        this.processCrossTabLogout(event.newValue);
      }
    };

    win.addEventListener('storage', listener);
    this.removeStorageListener = () => win.removeEventListener('storage', listener);
  }

  // Updates local activity timestamp and optionally syncs it across tabs.
  private updateActivity(forceSync = false): void {
    if (!this.idleTrackingActive) {
      return;
    }

    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    const now = Date.now();
    this.lastActivityTimestamp = now;

    if (forceSync || now - this.lastActivitySyncTimestamp >= this.activitySyncThrottleMs) {
      win.localStorage.setItem(this.idleLastActivityStorageKey, now.toString());
      this.lastActivitySyncTimestamp = now;
    }

    this.scheduleIdleCheck();
  }

  // Recomputes remaining idle time and schedules the next check.
  private scheduleIdleCheck(): void {
    if (!this.idleTrackingActive) {
      return;
    }

    if (!this.isIdleTimeoutEnabled()) {
      if (this.idleTimerId !== null) {
        const winRef = this.document.defaultView;
        winRef?.clearTimeout(this.idleTimerId);
        this.idleTimerId = null;
      }
      return;
    }

    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    if (this.idleTimerId !== null) {
      win.clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }

    const syncedTimestamp = Number(win.localStorage.getItem(this.idleLastActivityStorageKey));
    if (syncedTimestamp && syncedTimestamp > this.lastActivityTimestamp) {
      this.lastActivityTimestamp = syncedTimestamp;
    }

    const elapsed = Date.now() - this.lastActivityTimestamp;
    const remaining = this.idleTimeoutMs - elapsed;

    if (remaining <= 0) {
      this.triggerIdleLogout();
      return;
    }

    this.idleTimerId = win.setTimeout(() => this.scheduleIdleCheck(), Math.min(remaining, 60000));
  }

  // Performs local idle logout and notifies other tabs.
  private triggerIdleLogout(): void {
    this.broadcastLogoutEvent('idle-timeout');
    this.stopIdleTracking();
    this.store.dispatch(AuthStoreAction.logout());
  }

  // Broadcasts a logout event that other tabs can consume.
  private broadcastLogoutEvent(reason: 'idle-timeout' | 'auth-changed'): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    win.localStorage.setItem(
      this.logoutBroadcastStorageKey,
      JSON.stringify({
        sourceTabId: this.currentTabId,
        reason,
        timestamp: Date.now(),
      }),
    );
  }

  // Handles logout events received from another tab.
  private processCrossTabLogout(rawEvent: string): void {
    let eventData: { sourceTabId: string };

    try {
      eventData = JSON.parse(rawEvent);
    } catch (error) {
      return;
    }

    if (!eventData || eventData.sourceTabId === this.currentTabId || !this.isAuthenticated) {
      return;
    }

    this.stopIdleTracking();
    this.skipNextLogoutBroadcast = true;
    console.log('IdleTimeoutService - AUTO LOGOUT');
    this.store.dispatch(AuthStoreAction.logout());
  }

  // Returns true when this tab still has a valid local auth token.
  private isCurrentTabTokenValid(): boolean {
    const token = this.tokenService.getAuthToken();
    return !!token && token.isValid();
  }

  // Reads autologout timeout from preferences store and applies it live.
  private bindIdleTimeoutFromPreferences(): void {
    this.preferencesTimeoutSubscription = this.store
      .select(PreferencesSelectors.selectAutologoutTimeoutMinutes)
      .subscribe((timeoutMinutes) => {
        const normalizedMinutes = this.normalizeTimeoutMinutes(timeoutMinutes);
        const nextIdleTimeoutMs = normalizedMinutes * 60 * 1000;

        if (nextIdleTimeoutMs === this.idleTimeoutMs) {
          return;
        }

        this.idleTimeoutMs = nextIdleTimeoutMs;

        if (this.idleTrackingActive) {
          this.scheduleIdleCheck();
          return;
        }

        if (this.isAuthenticated && this.isIdleTimeoutEnabled()) {
          this.startIdleTracking();
        }
      });
  }

  // Normalizes missing values and keeps 0 as explicit "disabled" timeout.
  private normalizeTimeoutMinutes(timeoutMinutes: number): number {
    return timeoutMinutes != null && timeoutMinutes >= 0 ? timeoutMinutes : this.defaultIdleTimeoutMinutes;
  }

  // True when automatic idle logout is enabled (> 0 minutes).
  private isIdleTimeoutEnabled(): boolean {
    return this.idleTimeoutMs > 0;
  }

  // Guards browser-only logic (no-op in non-browser contexts).
  private hasWindow(): boolean {
    return !!this.document.defaultView;
  }
}
